import { type ReactNode, useEffect, useRef, useState } from 'react'

/**
 * Minimal popover: a trigger button and a panel that closes on outside click or
 * Escape. Deliberately dependency-free — the app needs three dropdowns, not a
 * component library.
 */
export function Popover({
  label,
  badge,
  children,
  align = 'left',
  width = 'w-64',
}: {
  label: ReactNode
  badge?: number
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
  width?: string
}) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={`border-line hover:border-line-strong flex items-center gap-1.5 border px-2.5 py-1.5 text-xs transition-colors ${
          badge ? 'bg-brand-soft border-brand/30 text-brand' : 'bg-surface text-muted'
        }`}
      >
        {label}
        {badge ? <span className="tnum font-mono text-[10px]">{badge}</span> : null}
        <svg width="8" height="5" viewBox="0 0 8 5" aria-hidden className="opacity-60">
          <path d="M0 0h8L4 5z" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div
          className={`bg-surface border-line absolute z-30 mt-1 border shadow-lg ${width} ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

/** Checkbox list used by every facet filter. */
export function CheckList({
  options,
  selected,
  onChange,
  emptyLabel = 'Keine Werte',
}: {
  options: { value: string; label: string; hint?: string }[]
  selected: string[]
  onChange: (next: string[]) => void
  emptyLabel?: string
}) {
  const toggle = (value: string) =>
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    )

  if (options.length === 0) {
    return <p className="text-faint px-3 py-3 text-xs">{emptyLabel}</p>
  }

  return (
    <div className="max-h-72 overflow-y-auto py-1">
      {options.map((option) => (
        <label
          key={option.value}
          className="hover:bg-canvas flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs"
        >
          <input
            type="checkbox"
            checked={selected.includes(option.value)}
            onChange={() => toggle(option.value)}
            className="accent-brand size-3.5 shrink-0"
          />
          <span className="text-ink min-w-0 flex-1 truncate">{option.label}</span>
          {option.hint && (
            <span className="text-faint tnum font-mono text-[10px]">{option.hint}</span>
          )}
        </label>
      ))}
    </div>
  )
}
